import { useState, useEffect, useRef } from 'react';
import { listOrgCollection, readOrgFile, writeOrgFile, deleteOrgFile } from '../../storage/FileSystemAdapter';
import { appendChangeLog } from '../../utils/changeLog';
import { scanReferencesToEntity } from '../../utils/intelligence';
import PeopleSelector, { resolvePerson } from '../shared/PeopleSelector';
import EntitySelector from '../shared/EntitySelector';
import InfoBadge from '../shared/InfoBadge';
import AISuggestButton from '../shared/AISuggestButton';
import { callWorker } from '../../utils/workerClient';
import { ORGANIZATION_TYPES, PUBLIC_SECTOR_EXPOSURE_VALUES } from '../../utils/enums';
import { areEquivalent } from '../../utils/legalEquivalents';
import { dateStatus } from '../../utils/dateStatus';
import { BG_SUBTLE, BG_HOVER_LT, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_MUTED, STATUS_GOOD, BG_SHELL, STATUS_BAD, STATUS_WARN, STATUS_INFO, STATUS_NEUTRAL, FIELD_ERROR_BORDER, UNSAVED_AMBER, FONT_PAGE_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON, FONT_LIST_ITEM, FONT_STAT_VALUE } from '../../theme';
import ConfirmDialog from '../shared/ConfirmDialog';
import useScrollToTopOnFormOpen from '../../utils/useScrollToTopOnFormOpen';

// ─── §11 RULE 42 — BLOCK DIALOG SUPPORT ──────────────────────────────────────
const HOLDER_NAV = {
  'risk': 'risks', 'asset': 'assets', 'supplier': 'suppliers',
  'incident': 'incidents', 'audit': 'audits', 'nonconformity': 'nonconformities',
  'objective': 'objectives', 'metric': 'metrics', 'document': 'documents',
  'legal-register': 'legal-register', 'management-review': 'management-reviews',
  'soa-control': 'soa', 'scope': 'scope',
};
function holderLabel(t, holderType, count) {
  const key = `deleteBlockedHolder_${holderType}_${count === 1 ? 'one' : 'other'}`;
  return t[key] || holderType;
}
function summariseHits(hits, t) {
  const counts = {};
  hits.forEach(h => { counts[h.holderType] = (counts[h.holderType] || 0) + 1; });
  return Object.entries(counts).map(([type, n]) => `${n} ${holderLabel(t, type, n)}`).join(', ');
}

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:                       'Registro legal',
    newEntry:                    '+ Nueva entrada',
    save:                        'Guardar',
    saving:                      'Guardando…',
    saveOk:                      '✓ Guardado',
    saveError:                   'Error al guardar',
    unsavedLabel:                'Cambios sin guardar',
    unsavedConfirm:              'Hay cambios sin guardar. ¿Salir de todas formas?',
    leaveWithoutSaving:          'Salir sin guardar',
    saveAndLeave:                'Guardar y salir',
    loading:                     'Cargando…',
    emptyTitle:                  'No hay entradas en el registro legal.',
    emptyHint:                   'Crea la primera entrada con el botón "+ Nueva entrada".',
    cntTotal:                    'Total',
    cntApplicable:               'Aplicables',
    cntUnderReview:              'En revisión',
    colRef:                      'Referencia',
    colTitle:                    'Título',
    colApplicable:               'Aplicable',
    colStatus:                   'Estado',
    colResponsible:              'Responsable',
    colReviewDate:               'Fecha revisión',
    colActions:                  'Acciones',
    viewBtn:                     'Ver',
    editBtn:                     'Editar',
    deleteBtn:                   'Eliminar',
    deleteFormBtn:               'Eliminar entrada',
    deleteConfirm:               '¿Eliminar la entrada "{ref}"? Esta acción no se puede deshacer.',
    deleteError:                 'Error al eliminar. Inténtalo de nuevo.',
    formTitleNew:                'Nueva entrada',
    formTitleEdit:               'Editar entrada',
    formTitleView:               'Ver entrada',
    backToList:                  '← Volver a la lista',
    sectionA:                    'Identificación',
    sectionB:                    'Relaciones',
    sectionC:                    'Gestión',
    fRef:                        'Referencia',
    fArticle:                    'Artículo / Sección',
    fJurisdiction:               'Jurisdicción',
    fTitle:                      'Título',
    fDescription:                'Descripción',
    fApplicable:                 'Aplicable',
    fApplicabilityJustification: 'Justificación de aplicabilidad',
    fLinkedControls:             'Controles del Anexo A vinculados',
    fLinkedDocuments:            'Documentos vinculados',
    fLinkedSuppliers:            'Proveedores vinculados',
    fLinkedAuthorities:          'Autoridades supervisoras vinculadas',
    ttLinkedAuthorities:         'Autoridades supervisoras de esta obligación legal. Útil para trazar incidentes y notificaciones reglamentarias al órgano de control que aplica.',
    authorityPlaceholder:        'Buscar autoridad…',
    authorityInactiveSuffix:     ' (Inactiva)',
    fTriggeringCustomers:        'Clientes que activan esta obligación',
    ttLinkedControls:            'Controles del Anexo A del SoA que satisfacen esta obligación legal. Permite trazabilidad obligación → control en auditoría.',
    ttLinkedDocuments:           'Políticas, procedimientos o registros documentales que evidencian el cumplimiento de esta obligación.',
    ttLinkedSuppliers:           'Proveedores sujetos a esta obligación o cuyo servicio depende de ella (típicamente encargados del tratamiento bajo GDPR / proveedores TIC bajo NIS2).',
    ttTriggeringCustomers:       'Cliente o clientes cuya presencia (por jurisdicción, sector o tipo de datos) hace que esta obligación legal sea aplicable a tu organización. Permite trazar la obligación al hecho concreto que la genera.',
    customerPlaceholder:         'Buscar cliente…',
    fResponsible:                'Responsable',
    fReviewDate:                 'Fecha de revisión',
    fStatus:                     'Estado',
    fNotes:                      'Notas',
    formSave:                    'Guardar entrada',
    formCancel:                  'Cancelar',
    validationError:             'Por favor completa los campos obligatorios marcados con *.',
    aiError:                     'No se pudo obtener sugerencia. Inténtalo de nuevo.',
    aiErrorOverloaded:           'Servidores ocupados, inténtalo de nuevo en un momento.',
    aiConsentRequired:           'Activa el procesamiento con IA en Configuración para usar esta función.',
    recommendationsTitle:        'Recomendaciones legales',
    recommendationsSummary:      'Sugerencias informativas de obligaciones legales según el contexto de la organización.',
    recommendationsHide:         'Ocultar',
    recommendationsShow:         'Mostrar',
    recommendationsLoading:      'Generando recomendaciones…',
    recommendationsDisclaimer:   'Esta lista es orientativa y no sustituye un análisis legal profesional para la jurisdicción y sector específicos de la organización.',
    recommendationsContextEmpty: 'El contexto AI no está definido. Las recomendaciones serán genéricas y posiblemente no se ajusten a la organización. Defínelo en Settings > AI context para mejores resultados.',
    aiAllCoveredFallback:        'Tu lista actual ya cubre todas las obligaciones legalmente vinculantes que aplican en el ámbito del SGSI según los datos disponibles.',
    descriptionPlaceholder:      'Resumen del marco legal: ámbito, requisitos clave y alcance aplicable.',
    badgeOverdue:                'Vencido',
    badgeSoon:                   'Vence pronto',
    statusActive:                'Activo',
    statusUnderReview:           'En revisión',
    statusSuperseded:            'Sustituido',
    applicableYes:               'Sí',
    applicableNo:                'No',
    filterApplicable:            'Aplicable',
    filterStatus:                'Estado',
    filterAll:                   'Todos',
    filterApplicableAll:         'Todos',
    filterApplicableYes:         'Sí',
    filterApplicableNo:          'No',
    controlPlaceholder:          'Buscar control…',
    documentPlaceholder:         'Buscar documento…',
    supplierPlaceholder:         'Buscar proveedor…',
    required:                    '*',
    requiredLegend:              '* Campos obligatorios',
    deleteBlockedTitle:        'No se puede eliminar: existen referencias',
    deleteBlockedReferencedBy: 'Referenciado por',
    deleteBlockedOpenHolder:   'Abrir',
    deactivateInstead:         'Desactivar en su lugar',
    bulkDeleteSummary_allBlocked: 'Ninguno de los registros seleccionados puede eliminarse porque todos están referenciados por otros registros.',
    bulkDeleteSummary_mixed:      '{deletable} de {total} registros pueden eliminarse; {blocked} están bloqueados por referencias.',
    continueWithDeletables:       'Eliminar los registros no bloqueados',
    deleteBlockedHolder_risk_one:               'riesgo',
    deleteBlockedHolder_risk_other:             'riesgos',
    deleteBlockedHolder_asset_one:              'activo',
    deleteBlockedHolder_asset_other:            'activos',
    deleteBlockedHolder_supplier_one:           'proveedor',
    deleteBlockedHolder_supplier_other:         'proveedores',
    deleteBlockedHolder_incident_one:           'incidente',
    deleteBlockedHolder_incident_other:         'incidentes',
    deleteBlockedHolder_audit_one:              'auditoría',
    deleteBlockedHolder_audit_other:            'auditorías',
    deleteBlockedHolder_nonconformity_one:      'no conformidad',
    deleteBlockedHolder_nonconformity_other:    'no conformidades',
    deleteBlockedHolder_objective_one:          'objetivo',
    deleteBlockedHolder_objective_other:        'objetivos',
    deleteBlockedHolder_metric_one:             'métrica',
    deleteBlockedHolder_metric_other:           'métricas',
    deleteBlockedHolder_document_one:           'documento',
    deleteBlockedHolder_document_other:         'documentos',
    'deleteBlockedHolder_legal-register_one':   'entrada del registro legal',
    'deleteBlockedHolder_legal-register_other': 'entradas del registro legal',
    'deleteBlockedHolder_management-review_one':   'revisión por la dirección',
    'deleteBlockedHolder_management-review_other': 'revisiones por la dirección',
    'deleteBlockedHolder_soa-control_one':      'control del SoA',
    'deleteBlockedHolder_soa-control_other':    'controles del SoA',
    deleteBlockedHolder_scope_one:              'alcance',
    deleteBlockedHolder_scope_other:            'alcance',
  },
  en: {
    title:                       'Legal register',
    newEntry:                    '+ New entry',
    save:                        'Save',
    saving:                      'Saving…',
    saveOk:                      '✓ Saved',
    saveError:                   'Save error',
    unsavedLabel:                'Unsaved changes',
    unsavedConfirm:              'You have unsaved changes. Leave anyway?',
    leaveWithoutSaving:          'Leave without saving',
    saveAndLeave:                'Save and leave',
    loading:                     'Loading…',
    emptyTitle:                  'No legal register entries yet.',
    emptyHint:                   'Create the first entry using the "+ New entry" button.',
    cntTotal:                    'Total',
    cntApplicable:               'Applicable',
    cntUnderReview:              'Under review',
    colRef:                      'Reference',
    colTitle:                    'Title',
    colApplicable:               'Applicable',
    colStatus:                   'Status',
    colResponsible:              'Responsible',
    colReviewDate:               'Review date',
    colActions:                  'Actions',
    viewBtn:                     'View',
    editBtn:                     'Edit',
    deleteBtn:                   'Delete',
    deleteFormBtn:               'Delete entry',
    deleteConfirm:               'Delete entry "{ref}"? This cannot be undone.',
    deleteError:                 'Delete failed. Please try again.',
    formTitleNew:                'New entry',
    formTitleEdit:               'Edit entry',
    formTitleView:               'View entry',
    backToList:                  '← Back to list',
    sectionA:                    'Identification',
    sectionB:                    'Relationships',
    sectionC:                    'Management',
    fRef:                        'Reference',
    fArticle:                    'Article / Section',
    fJurisdiction:               'Jurisdiction',
    fTitle:                      'Title',
    fDescription:                'Description',
    fApplicable:                 'Applicable',
    fApplicabilityJustification: 'Applicability justification',
    fLinkedControls:             'Linked Annex A controls',
    fLinkedDocuments:            'Linked documents',
    fLinkedSuppliers:            'Linked suppliers',
    fLinkedAuthorities:          'Linked supervisory authorities',
    ttLinkedAuthorities:         'Supervisory authorities for this legal obligation. Useful for tracing incidents and statutory notifications to the applicable regulator.',
    authorityPlaceholder:        'Search authority…',
    authorityInactiveSuffix:     ' (Inactive)',
    fTriggeringCustomers:        'Customers that trigger this obligation',
    ttLinkedControls:            'SoA Annex A controls that satisfy this legal obligation. Provides obligation → control traceability for the audit.',
    ttLinkedDocuments:           'Policies, procedures or documented records that evidence compliance with this obligation.',
    ttLinkedSuppliers:           'Suppliers subject to this obligation or whose service depends on it (typically GDPR processors or NIS2 ICT suppliers).',
    ttTriggeringCustomers:       'Customer(s) whose presence (by jurisdiction, sector or data type) makes this legal obligation applicable to your organization. Lets you trace the obligation back to the concrete fact that triggers it.',
    customerPlaceholder:         'Search customer…',
    fResponsible:                'Responsible person',
    fReviewDate:                 'Review date',
    fStatus:                     'Status',
    fNotes:                      'Notes',
    formSave:                    'Save entry',
    formCancel:                  'Cancel',
    validationError:             'Please complete the required fields marked with *.',
    aiError:                     'Could not get suggestion. Please try again.',
    aiErrorOverloaded:           'Servers are busy, please try again in a moment.',
    aiConsentRequired:           'Enable AI processing in Settings to use this feature.',
    recommendationsTitle:        'Legal recommendations',
    recommendationsSummary:      'Informational suggestions of legal obligations based on the organization’s context.',
    recommendationsHide:         'Hide',
    recommendationsShow:         'Show',
    recommendationsLoading:      'Generating recommendations…',
    recommendationsDisclaimer:   "This list is indicative and does not replace a professional legal analysis for the organization's specific jurisdiction and sector.",
    recommendationsContextEmpty: 'AI context is not defined. Recommendations will be generic and may not fit the organization. Set it in Settings > AI context for better results.',
    aiAllCoveredFallback:        'Your current list already covers all legally binding obligations within the ISMS scope that apply based on the available inputs.',
    descriptionPlaceholder:      'Summary of the legal framework: scope, key requirements and applicable reach.',
    badgeOverdue:                'Overdue',
    badgeSoon:                   'Due soon',
    statusActive:                'Active',
    statusUnderReview:           'Under Review',
    statusSuperseded:            'Superseded',
    applicableYes:               'Yes',
    applicableNo:                'No',
    filterApplicable:            'Applicable',
    filterStatus:                'Status',
    filterAll:                   'All',
    filterApplicableAll:         'All',
    filterApplicableYes:         'Yes',
    filterApplicableNo:          'No',
    controlPlaceholder:          'Search control…',
    documentPlaceholder:         'Search document…',
    supplierPlaceholder:         'Search supplier…',
    required:                    '*',
    requiredLegend:              '* Required fields',
    deleteBlockedTitle:        'Cannot delete: references found',
    deleteBlockedReferencedBy: 'Referenced by',
    deleteBlockedOpenHolder:   'Open',
    deactivateInstead:         'Deactivate instead',
    bulkDeleteSummary_allBlocked: 'None of the selected records can be deleted because all of them are referenced by other records.',
    bulkDeleteSummary_mixed:      '{deletable} of {total} records can be deleted; {blocked} are blocked by references.',
    continueWithDeletables:       'Delete the unblocked records',
    deleteBlockedHolder_risk_one:               'risk',
    deleteBlockedHolder_risk_other:             'risks',
    deleteBlockedHolder_asset_one:              'asset',
    deleteBlockedHolder_asset_other:            'assets',
    deleteBlockedHolder_supplier_one:           'supplier',
    deleteBlockedHolder_supplier_other:         'suppliers',
    deleteBlockedHolder_incident_one:           'incident',
    deleteBlockedHolder_incident_other:         'incidents',
    deleteBlockedHolder_audit_one:              'audit',
    deleteBlockedHolder_audit_other:            'audits',
    deleteBlockedHolder_nonconformity_one:      'nonconformity',
    deleteBlockedHolder_nonconformity_other:    'nonconformities',
    deleteBlockedHolder_objective_one:          'objective',
    deleteBlockedHolder_objective_other:        'objectives',
    deleteBlockedHolder_metric_one:             'metric',
    deleteBlockedHolder_metric_other:           'metrics',
    deleteBlockedHolder_document_one:           'document',
    deleteBlockedHolder_document_other:         'documents',
    'deleteBlockedHolder_legal-register_one':   'legal register entry',
    'deleteBlockedHolder_legal-register_other': 'legal register entries',
    'deleteBlockedHolder_management-review_one':   'management review',
    'deleteBlockedHolder_management-review_other': 'management reviews',
    'deleteBlockedHolder_soa-control_one':      'SoA control',
    'deleteBlockedHolder_soa-control_other':    'SoA controls',
    deleteBlockedHolder_scope_one:              'scope',
    deleteBlockedHolder_scope_other:            'scope',
  },
};

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const LEGAL_FRAMEWORKS = ['GDPR', 'LOPDGDD', 'NIS2', 'eIDAS', 'DORA', 'AI Act', 'Reglamento de Datos', 'ISO/IEC 27701', 'HIPAA', 'LGPD', 'SOX', 'PCI DSS'];
const LEGAL_JURISDICTIONS = [
  { es: 'Unión Europea',                en: 'European Union' },
  { es: 'Espacio Económico Europeo',    en: 'European Economic Area' },
  { es: 'Mercosur',                     en: 'Mercosur' },
  { es: 'Comunidad Andina',             en: 'Andean Community' },
  { es: 'España (Estado)',              en: 'Spain (national)' },
  { es: 'España — Andalucía',           en: 'Spain — Andalusia' },
  { es: 'España — Cataluña',            en: 'Spain — Catalonia' },
  { es: 'España — Madrid',              en: 'Spain — Madrid' },
  { es: 'España — País Vasco',          en: 'Spain — Basque Country' },
  { es: 'España — Galicia',             en: 'Spain — Galicia' },
  { es: 'España — Comunidad Valenciana',en: 'Spain — Valencia' },
  { es: 'México',                       en: 'Mexico' },
  { es: 'Brasil',                       en: 'Brazil' },
  { es: 'Argentina',                    en: 'Argentina' },
  { es: 'Colombia',                     en: 'Colombia' },
  { es: 'Chile',                        en: 'Chile' },
  { es: 'Perú',                         en: 'Peru' },
  { es: 'Uruguay',                      en: 'Uruguay' },
  { es: 'Costa Rica',                   en: 'Costa Rica' },
  { es: 'Estados Unidos (Federal)',     en: 'United States (Federal)' },
  { es: 'EE.UU. — California',          en: 'US — California' },
  { es: 'EE.UU. — Nueva York',          en: 'US — New York' },
  { es: 'EE.UU. — Texas',               en: 'US — Texas' },
  { es: 'EE.UU. — Delaware',            en: 'US — Delaware' },
  { es: 'Reino Unido',                  en: 'United Kingdom' },
  { es: 'Suiza',                        en: 'Switzerland' },
  { es: 'Canadá',                       en: 'Canada' },
  { es: 'Canadá — Quebec',              en: 'Canada — Quebec' },
];
const aId = (id) => id ? `A.${id}` : id;

// ── STATUS COLORS ─────────────────────────────────────────────────────────────
const LR_STATUS_COLORS = {
  'Active':       { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
  'Under Review': { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Superseded':   { bg: BG_MUTED, text: TEXT_MUTED },
};

// ── ID GENERATION ─────────────────────────────────────────────────────────────
async function genId(orgId, lrPrefix) {
  const now   = new Date();
  const year  = now.getFullYear();
  const mm    = String(now.getMonth() + 1).padStart(2, '0');
  const dd    = String(now.getDate()).padStart(2, '0');
  const prefix = `${lrPrefix}-${year}-`;
  try {
    const items = await listOrgCollection(orgId, 'legal-register');
    let max = 0;
    items.forEach(e => {
      if (e.ref && e.ref.startsWith(prefix)) {
        const n = parseInt(e.ref.slice(prefix.length), 10);
        if (!isNaN(n) && n > max) max = n;
      }
    });
    const seq = String(max + 1).padStart(3, '0');
    return { fileId: `lr-${year}${mm}${dd}-${seq}`, ref: `${prefix}${seq}` };
  } catch {
    return { fileId: `lr-${year}${mm}${dd}-001`, ref: `${prefix}001` };
  }
}

// ── BLANK FORM ────────────────────────────────────────────────────────────────
function blankForm(ref) {
  return {
    ref,
    article:                   '',
    jurisdiction:              '',
    title:                     '',
    description:               '',
    applicable:                true,
    applicabilityJustification:'',
    linkedControlIds:          [],
    linkedDocumentIds:         [],
    linkedSupplierIds:         [],
    linkedAuthorityIds:        [],
    triggeringCustomerIds:     [],
    responsiblePerson:         null,
    reviewDate:                '',
    status:                    'Active',
    notes:                     '',
  };
}

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const inputSt = (err) => ({
  ...FONT_LABEL,
  width: '100%', boxSizing: 'border-box',
  padding: '0.45rem 0.65rem',
  border: `1px solid ${err ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
  borderRadius: 6,
  background: BG_SURFACE, color: TEXT_DEFAULT, outline: 'none',
});

const labelSt = {
  ...FONT_SMALL,
  color: TEXT_MUTED, letterSpacing: '0.03em',
  textTransform: 'uppercase', display: 'block', marginBottom: '0.3rem',
};

const thSt = {
  ...FONT_TINY,
  color: TEXT_MUTED, letterSpacing: '0.04em', textTransform: 'uppercase',
  padding: '0.55rem 0.7rem', textAlign: 'left',
  borderBottom: `1px solid ${BORDER_DEFAULT}`, background: BG_SUBTLE,
  whiteSpace: 'nowrap',
};

const tdSt = {
  ...FONT_LIST_ITEM,
  color: TEXT_DEFAULT, padding: '0.65rem 0.7rem',
  borderBottom: `1px solid ${BG_SHELL}`, verticalAlign: 'middle',
};

// ── SUB-COMPONENTS ────────────────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <div style={{
      ...FONT_BODY_STRONG,
      color: TEXT_HEADING,
      marginBottom: '1rem', paddingBottom: '0.5rem',
      borderBottom: `1px solid ${BORDER_DEFAULT}`,
    }}>
      {children}
    </div>
  );
}

function Divider() {
  return <div style={{ borderTop: `1px solid ${BORDER_DEFAULT}`, margin: '1.5rem 0' }} />;
}

function LRStatusBadge({ status, t }) {
  const colors = LR_STATUS_COLORS[status] || LR_STATUS_COLORS['Active'];
  const labels = {
    'Active':       t.statusActive,
    'Under Review': t.statusUnderReview,
    'Superseded':   t.statusSuperseded,
  };
  return (
    <span style={{
      background: colors.bg, color: colors.text,
      borderRadius: 5, padding: '0.2rem 0.55rem',
      ...FONT_SMALL, fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {labels[status] || status}
    </span>
  );
}

function ApplicableBadge({ applicable, t }) {
  return (
    <span style={{
      background: applicable ? STATUS_GOOD.bg : ERROR_BG,
      color:      applicable ? BRAND_PRIMARY_DARK : FIELD_ERROR_BORDER, // TODO: P6b — verify color role
      borderRadius: 5, padding: '0.2rem 0.55rem',
      ...FONT_SMALL, fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {applicable ? t.applicableYes : t.applicableNo}
    </span>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-unused-vars
export default function LegalRegisterView({ org, lang, onBack, onNavigate, onDirtyChange, saveRequestTick: appSaveRequestTick, onSaveResult }) {
  const t = T[lang] || T.en;

  const [entries, setEntries]               = useState(null);
  const [people, setPeople]                 = useState([]);
  const [controlOptions, setControlOptions] = useState([]);
  const [documentOptions,  setDocumentOptions]  = useState([]);
  const [supplierOptions,  setSupplierOptions]   = useState([]);
  const [customerOptions,  setCustomerOptions]   = useState([]);
  const [authorityOptions, setAuthorityOptions]  = useState([]);

  const [formSaveState, setFormSaveState]   = useState('idle');
  const [formOpen, setFormOpen]             = useState(false);
  const [formMode, setFormMode]             = useState('edit');
  const [editingId, setEditingId]           = useState(null);
  const [form, setForm]                     = useState(null);
  const [formErrors, setFormErrors]         = useState({});
  const [dirty, setDirty]                   = useState(false);
  const [saveRequestTick, setSaveRequestTick] = useState(0);
  const [aiSuggesting, setAiSuggesting]     = useState(false);
  const [aiError, setAiError]               = useState(null);

  // Recommendations banner — collapsible, in-memory only (not persisted)
  const [recExpanded, setRecExpanded]       = useState(false);
  const [recItems, setRecItems]             = useState([]);
  const [recFallback, setRecFallback]       = useState('');
  const [recLoading, setRecLoading]         = useState(false);
  const [recError, setRecError]             = useState(null);
  // App-level "Save and leave" — flagged when the App tick increases so
  // the next form save reports back via onSaveResult instead of running
  // the form-Cancel close-form path. See GRC_UI_PATTERNS.md §5.
  const pendingAppSaveRef                   = useRef(false);
  const lastProcessedAppTickRef             = useRef(0);
  const pendingFileIdRef                    = useRef(null);

  const [deleteError, setDeleteError]       = useState(null);
  const [confirmState, setConfirmState]     = useState(null);
  const [scanningDelete, setScanningDelete] = useState(false);

  const [filterApplicable, setFilterApplicable] = useState('');
  const [filterStatus, setFilterStatus]     = useState('');

  useScrollToTopOnFormOpen(editingId !== null);

  // ── Load entries ──────────────────────────────────────────────────────────
  useEffect(() => {
    listOrgCollection(org.id, 'legal-register')
      .then(items => setEntries([...items].sort((a, b) => (a.ref || '').localeCompare(b.ref || ''))))
      .catch(() => setEntries([]));
  }, [org.id]);

  // ── Load people ───────────────────────────────────────────────────────────
  useEffect(() => {
    readOrgFile(org.id, 'people.json')
      .then(data => setPeople(Array.isArray(data.people) ? data.people : []))
      .catch(() => setPeople([]));
  }, [org.id]);

  // ── Load relation options ─────────────────────────────────────────────────
  useEffect(() => {
    readOrgFile(org.id, 'controls/soa.json')
      .then(soa => setControlOptions((soa.controls || []).map(c => ({ id: c.id, label: `${aId(c.id)} — ${lang === 'es' ? (c.title_es || c.title) : c.title}` }))))
      .catch(() => setControlOptions([]));
    listOrgCollection(org.id, 'documents')
      .then(items => setDocumentOptions(items.map(d => ({ id: d.id, label: d.title || d.ref || d.id }))))
      .catch(() => setDocumentOptions([]));
    listOrgCollection(org.id, 'suppliers')
      .then(items => setSupplierOptions(items.map(s => ({ id: s.id, label: s.name || s.ref || s.id }))))
      .catch(() => setSupplierOptions([]));
    listOrgCollection(org.id, 'customers')
      .then(items => setCustomerOptions(items.map(c => ({ id: c.id, label: c.name || c.ref || c.id }))))
      .catch(() => setCustomerOptions([]));
    listOrgCollection(org.id, 'authorities')
      .then(items => {
        const sorted = [...items].sort((x, y) => (x.ref || '').localeCompare(y.ref || ''));
        const labelSuffix = lang === 'es' ? ' (Inactiva)' : ' (Inactive)';
        setAuthorityOptions(sorted.map(au => ({
          id: au.id,
          label: `${au.ref || au.id} — ${au.shortName || au.name || au.id}${au.status === 'Inactive' ? labelSuffix : ''}`,
        })));
      })
      .catch(() => setAuthorityOptions([]));
  }, [org.id, lang]);

  // ── Field helper ──────────────────────────────────────────────────────────
  const setF = (field, val) => {
    if (formMode !== 'view') setDirty(true);
    setForm(prev => prev ? { ...prev, [field]: val } : prev);
  };

  const clearErr = (field) => {
    if (formErrors[field]) setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  // ── App-level guard: bridge App tick into local tick (which the existing
  //    saveRequestTick watcher below picks up to call handleFormSave).
  //    See GRC_UI_PATTERNS.md §5.
  useEffect(() => {
    if (typeof appSaveRequestTick !== 'number') return;
    if (appSaveRequestTick <= lastProcessedAppTickRef.current) return;
    lastProcessedAppTickRef.current = appSaveRequestTick;
    if (formOpen) {
      pendingAppSaveRef.current = true;
      setSaveRequestTick(n => n + 1);
    } else {
      onSaveResult?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSaveRequestTick]);

  // ── beforeunload guard ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── Save request watcher (triggered by "Save and leave") ──────────────────
  useEffect(() => {
    if (saveRequestTick > 0) { handleFormSave(); setSaveRequestTick(0); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  // ── Form open / close ─────────────────────────────────────────────────────
  const entryToForm = (entry) => ({
    ref:                        entry.ref || '',
    article:                    entry.article || '',
    jurisdiction:               entry.jurisdiction || '',
    title:                      entry.title || '',
    description:                entry.description || '',
    applicable:                 entry.applicable !== false,
    applicabilityJustification: entry.applicabilityJustification || '',
    linkedControlIds:           Array.isArray(entry.linkedControlIds)  ? entry.linkedControlIds  : [],
    linkedDocumentIds:          Array.isArray(entry.linkedDocumentIds) ? entry.linkedDocumentIds : [],
    linkedSupplierIds:          Array.isArray(entry.linkedSupplierIds) ? entry.linkedSupplierIds : [],
    linkedAuthorityIds:         Array.isArray(entry.linkedAuthorityIds) ? entry.linkedAuthorityIds : [],
    triggeringCustomerIds:      Array.isArray(entry.triggeringCustomerIds) ? entry.triggeringCustomerIds : [],
    responsiblePerson:          entry.responsiblePerson || null,
    reviewDate:                 entry.reviewDate || '',
    status:                     entry.status || 'Active',
    notes:                      entry.notes || '',
  });

  const openNew = async () => {
    const lrPrefix = (org.refPrefixes && org.refPrefixes.legalRegister) || 'lrg';
    const { fileId, ref } = await genId(org.id, lrPrefix);
    pendingFileIdRef.current = fileId;
    const newForm = blankForm(ref);
    setForm(newForm);
    setFormMode('create');
    setEditingId(null);
    setFormErrors({});
    setFormOpen(true);
    setDirty(false);
  };

  const openEdit = (entry) => {
    setForm(entryToForm(entry));
    setFormMode('edit');
    setEditingId(entry.id);
    setFormErrors({});
    setFormOpen(true);
    setDirty(false);
  };

  const openView = (entry) => {
    setForm(entryToForm(entry));
    setFormMode('view');
    setEditingId(entry.id);
    setFormErrors({});
    setFormOpen(true);
  };

  const closeForm = () => {
    setDirty(false);
    setFormOpen(false);
    setForm(null);
    setFormErrors({});
  };

  const handleFormCancel = () => {
    if (dirty) {
      setConfirmState({
        message: t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm: () => { setConfirmState(null); closeForm(); },
        secondaryAction: {
          label: t.saveAndLeave,
          onClick: () => {
            setConfirmState(null);
            setSaveRequestTick(n => n + 1);
          },
        },
      });
      return;
    }
    closeForm();
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleFormSave = async () => {
    const errs = {};
    if (!form.ref?.trim())   errs.ref = true;
    if (!form.title?.trim()) errs.title = true;
    if (!form.applicable && !form.applicabilityJustification?.trim()) {
      errs.applicabilityJustification = true;
    }

    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      return;
    }

    const now       = new Date().toISOString();
    const changedBy = org.consultantPersonId || 'system';
    setFormSaveState('saving');

    try {
      let fileId;
      let obj;

      if (editingId === null) {
        fileId = pendingFileIdRef.current || `lr-${form.ref.trim().toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
        obj = {
          id:                         fileId,
          orgId:                      org.id,
          ref:                        form.ref.trim(),
          article:                    form.article.trim(),
          jurisdiction:               form.jurisdiction.trim(),
          title:                      form.title.trim(),
          description:                form.description.trim(),
          applicable:                 form.applicable,
          applicabilityJustification: form.applicabilityJustification.trim(),
          linkedControlIds:           form.linkedControlIds,
          linkedDocumentIds:          form.linkedDocumentIds,
          linkedSupplierIds:          form.linkedSupplierIds,
          linkedAuthorityIds:         form.linkedAuthorityIds,
          triggeringCustomerIds:      form.triggeringCustomerIds,
          responsiblePerson:          form.responsiblePerson || null,
          reviewDate:                 form.reviewDate || null,
          status:                     form.status,
          notes:                      form.notes.trim(),
          createdAt:                  now,
          updatedAt:                  now,
          changeLog:                  [],
        };
      } else {
        fileId = editingId;
        const existing = (entries || []).find(e => e.id === editingId) || {};
        obj = {
          ...existing,
          id:                         fileId,
          orgId:                      org.id,
          ref:                        form.ref.trim(),
          article:                    form.article.trim(),
          jurisdiction:               form.jurisdiction.trim(),
          title:                      form.title.trim(),
          description:                form.description.trim(),
          applicable:                 form.applicable,
          applicabilityJustification: form.applicabilityJustification.trim(),
          linkedControlIds:           form.linkedControlIds,
          linkedDocumentIds:          form.linkedDocumentIds,
          linkedSupplierIds:          form.linkedSupplierIds,
          linkedAuthorityIds:         form.linkedAuthorityIds,
          triggeringCustomerIds:      form.triggeringCustomerIds,
          responsiblePerson:          form.responsiblePerson || null,
          reviewDate:                 form.reviewDate || null,
          status:                     form.status,
          notes:                      form.notes.trim(),
          updatedAt:                  now,
        };
        obj.changeLog = appendChangeLog(existing, obj, changedBy);
      }

      await writeOrgFile(org.id, `legal-register/${fileId}.json`, obj);

      const items = await listOrgCollection(org.id, 'legal-register');
      setEntries([...items].sort((a, b) => (a.ref || '').localeCompare(b.ref || '')));

      if (editingId === null) {
        setEditingId(fileId);
        setFormMode('edit');
      }
      setDirty(false);
      setFormErrors({});
      setFormSaveState('ok');
      setTimeout(() => {
        setFormSaveState('idle');
      }, 2000);
      if (pendingAppSaveRef.current) {
        pendingAppSaveRef.current = false;
        onSaveResult?.(true);
      }
    } catch {
      if (pendingAppSaveRef.current) {
        pendingAppSaveRef.current = false;
        onSaveResult?.(false);
      }
      setFormSaveState('error');
      setTimeout(() => setFormSaveState('idle'), 3000);
    }
  };

  // ── AI suggest: applicability justification ──────────────────────────────
  const handleAISuggestApplicability = async () => {
    setAiSuggesting(true);
    setAiError(null);
    const sector       = org.sector       || '';
    const size         = org.size         || '';
    const country      = Array.isArray(org.country) ? org.country.join(',') : (org.country || '');
    const technologies = Array.isArray(org.technologies) ? org.technologies.join(', ') : (org.technologies || '');
    const aiContext    = org.aiContext    || '';
    const organizationType                = (typeof org.organizationType === 'string' && ORGANIZATION_TYPES.includes(org.organizationType)) ? org.organizationType : 'private';
    const publicSectorExposure            = (typeof org.publicSectorExposure === 'string' && PUBLIC_SECTOR_EXPOSURE_VALUES.includes(org.publicSectorExposure)) ? org.publicSectorExposure : 'none';
    const publicSectorExposureDescription = typeof org.publicSectorExposureDescription === 'string' ? org.publicSectorExposureDescription : '';
    const applicable   = form.applicable ? 'yes' : 'no';
    const prompt = `You are an information security and legal compliance expert.
Given the organization profile and a legal framework entry, write a 2–4 sentence justification of applicability.
Respond ONLY with a valid JSON object, no markdown, no preamble.
Organization sector: ${sector}
Organization size: ${size}
Organization country: ${country}
Legal jurisdiction: ${form.jurisdiction || 'Not specified'}
Organization technologies: ${technologies}
Organization type (public, private or mixed entity): ${organizationType}
Public-sector exposure (for private or mixed orgs; 'none' if no exposure, 'contractor' if the organization is a contractor to public-sector entities, 'critical_service_provider' if it provides critical services to the public sector, 'mixed_subordinate' if it is a mixed-ownership entity subordinate to a public parent): ${publicSectorExposure}
Public-sector exposure narrative (sanitised free text describing the specific exposure, may be empty): ${publicSectorExposureDescription || 'Not specified'}
Organization context: ${aiContext}
Legal framework title: ${form.title}
Applicable: ${applicable}
The "Legal jurisdiction" is the territorial scope of the obligation (e.g., a supranational regime like an EU directive, a national law, a US state law, or a global instrument). When applicable=yes, the justification must take that scope into account and explain why this legal framework applies to the organization given its sector, size, country, jurisdiction, technologies and context. When applicable=no, the justification must take that scope into account too and explain why it does not apply (for example, an obligation scoped to US — California does not apply to a Spain-only organization).
Return exactly this JSON structure:
{
"applicabilityJustification": "string"
}`;
    const result = await callWorker({ prompt, maxTokens: 600 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiError(busy ? t.aiErrorOverloaded : t.aiError);
      setAiSuggesting(false);
      return;
    }
    let parsed;
    try { parsed = JSON.parse(result.text); } catch {
      setAiError(t.aiError);
      setAiSuggesting(false);
      return;
    }
    if (typeof parsed.applicabilityJustification === 'string' && !form.applicabilityJustification.trim()) {
      setF('applicabilityJustification', parsed.applicabilityJustification);
    }
    setAiSuggesting(false);
  };

  // ── AI recommendations: candidate frameworks vs. covered ─────────────────
  const handleGenerateRecommendations = async () => {
    if (recLoading) return;
    if (!org.aiProcessingConsent) return;
    setRecLoading(true);
    setRecError(null);
    setRecExpanded(true);
    setRecItems([]);
    setRecFallback('');

    const sector       = org.sector       || '';
    const size         = org.size         || '';
    const country      = Array.isArray(org.country) ? org.country.join(',') : (org.country || '');
    const technologies = Array.isArray(org.technologies)
      ? (org.technologies.join(', ') || 'Not specified')
      : (org.technologies || 'Not specified');
    const langName     = org.language === 'es' ? 'Spanish' : 'English';
    const aiContext    = org.aiContext    || '';
    const organizationType                = (typeof org.organizationType === 'string' && ORGANIZATION_TYPES.includes(org.organizationType)) ? org.organizationType : 'private';
    const publicSectorExposure            = (typeof org.publicSectorExposure === 'string' && PUBLIC_SECTOR_EXPOSURE_VALUES.includes(org.publicSectorExposure)) ? org.publicSectorExposure : 'none';
    const publicSectorExposureDescription = typeof org.publicSectorExposureDescription === 'string' ? org.publicSectorExposureDescription : '';
    const aiContextEmpty = !((org.aiContext || '').trim());
    const coveredTitles = (entries || [])
      .filter(e => e.applicable === true)
      .map(e => (e.title || '').trim())
      .filter(Boolean)
      .join(', ');

    const contextNote = aiContextEmpty
      ? 'Context note: the organization has not provided contextual notes. Produce a general overview based ONLY on country and sector. Do not assume regulated activities (healthcare, finance, public-sector, critical infrastructure, telecom) unless the sector field itself indicates them.'
      : "Context note: ground every recommendation in the organization context above. Do not invent regulated activities that are not stated. If the context does not indicate that the organization is a public-sector supplier, do not suggest public-sector laws. If the context does not indicate that the organization handles health data, do not suggest health-sector laws. Apply the same conservative principle to all sector-specific regimes.";

    const prompt = `You are an expert in information security, cybersecurity and data privacy regulatory compliance.
The application you are assisting manages an Information Security Management System (ISMS) per ISO/IEC 27001:2022. Recommendations must be scoped EXCLUSIVELY to legally binding obligations whose subject-matter domain is information security, cybersecurity, data protection or privacy.

IN SCOPE — recommend ONLY legally binding instruments whose subject matter is:
- personal data protection and privacy (e.g. GDPR, LOPDGDD, CCPA, LGPD, BDSG, Argentina Ley 25.326)
- cybersecurity and information security (e.g. NIS2, DORA, EU Cyber Resilience Act, NY SHIELD Act, Texas Business & Commerce §521, CIRCIA, China Cybersecurity Law)
- security incident and personal data breach notification (any jurisdiction)
- sector-specific privacy or information security regimes (e.g. HIPAA for healthcare, GLBA for finance, FERPA for education, PCI DSS as a binding contractual framework where the inputs imply payment card data)
- cybersecurity of critical infrastructure
- electronic communications privacy, retention of communications and logs, lawful interception, and workplace monitoring of personal data
- export controls specific to cybersecurity or cryptographic technology (e.g. Wassenaar Arrangement, EAR ECCN 5A002, dual-use cyber items)
- national security information classification regimes that govern handling of restricted information (e.g. NIST SP 800-171 for US defense contractors, Spanish Ley de Secretos Oficiales)

OUT OF SCOPE — do NOT recommend any of the following, even when the input sector or jurisdiction would normally trigger them:
- process safety, occupational health and safety (e.g. OSHA PSM, Seveso III, ATEX, MAH regulations)
- environmental protection (e.g. Clean Air Act, CERCLA, REACH, water and soil regulations)
- general labor and employment law (consider it only where it specifically governs employee personal data or workplace digital surveillance)
- tax, corporate and securities law (consider only specifically cyber-related securities disclosure rules such as SEC Item 1.05 on material cybersecurity incidents)
- general commercial or contract law (PCI DSS is the only contractual framework in scope)
- building codes, transport safety and product safety (consider only specifically cyber-related rules such as TSA pipeline cybersecurity directives)
- voluntary standards and frameworks (ISO 27001, ISO 27002, ISO 27701, SOC 2, NIST CSF, NIST 800-53, COBIT, CIS Controls, ENISA guidance, ISF, or any similar)

Treat the IN SCOPE and OUT OF SCOPE lists as exhaustive in their respective directions. If an obligation does not clearly fall into an IN SCOPE category, omit it.

Now identify legally binding obligations within the IN SCOPE list that plausibly apply to the organization described below, and compare them to the obligations the organization has already marked as applicable.
Sector: ${sector || 'Not specified'}
Size (employees): ${size || 'Not specified'}
Country: ${country || 'Not specified'}
Technologies: ${technologies}
Organization type (public, private or mixed entity): ${organizationType}
Public-sector exposure (for private or mixed orgs; 'none' if no exposure, 'contractor' if the organization is a contractor to public-sector entities, 'critical_service_provider' if it provides critical services to the public sector, 'mixed_subordinate' if it is a mixed-ownership entity subordinate to a public parent): ${publicSectorExposure}
Public-sector exposure narrative (sanitised free text describing the specific exposure, may be empty): ${publicSectorExposureDescription || 'Not specified'}
Organization context: ${aiContext || 'Not specified'}
Already applicable obligations: ${coveredTitles || 'None'}
${contextNote}
Do not include any obligation that depends on facts not stated in the inputs above.
Limit the recommendation to at most ten obligations. Recommend only obligations that genuinely apply; if fewer than ten apply, return only those that apply, and do not pad, stretch, or invent obligations to approach ten.
The bulleted list contains EXCLUSIVELY obligations that are NOT in the 'Already applicable obligations' input.
When deciding whether a candidate is already covered, treat as equivalent: the official long name, common short forms, acronyms, abbreviations, and informal local names of the same legal instrument. Examples of equivalent pairs that must be treated as the same obligation: 'LOPDGDD' and 'Ley Orgánica 3/2018 de Protección de Datos Personales y Garantía de los Derechos Digitales'; 'GDPR' and 'Reglamento General de Protección de Datos'; 'NIS2' and 'Directive (EU) 2022/2555'. If a candidate matches any name variant of an already-covered obligation, omit it entirely from the output. Do not enumerate it. Do not annotate it. Do not reference it.
Do not provide per-obligation justifications, parenthetical explanations, citations, or examples of regulated activities.
Do not annotate any bullet item with parentheticals, status notes, or cross-references such as '(already listed)', '(skip)', '(see below)', '(ya cubierta)', '(ya registrada)', or any equivalent. Each bullet contains only the obligation name.
List ALL legally binding obligations within the IN SCOPE list that apply to the organization based on the inputs above. Do not omit an obligation because it is widely-known, common, default, or otherwise assumed in the organization's jurisdiction. If GDPR applies because the organization is established in the EU or processes the personal data of EU residents, list it explicitly. If a sector-specific framework applies because the organization's sector triggers it (e.g. HIPAA for Healthcare, GLBA for Finance, FERPA for Education, PCI DSS where payment card data is processed), list it explicitly. The deduplication rule against the 'Already applicable obligations' input applies AFTER this listing rule: list everything that applies, then exclude items that match anything in 'Already applicable obligations' as instructed elsewhere in this prompt.
Return your response in exactly ONE of the two shapes below. Choose exactly ONE of the two shapes. Do not combine them. Do not introduce any other shape.
Shape A — at least one missing obligation: a bulleted list, one obligation per line, each line starting with "- " (a hyphen followed by a single space) and the obligation name with no annotation, parenthetical status note, or cross-reference of any kind. Up to ten lines. No surrounding sentence, no preamble, no trailing summary line, no per-item justification or parenthetical.
Shape B — no missing obligations: a single plain-text sentence stating that the organization's existing list already covers all the IN SCOPE legally binding obligations that apply. The sentence must NOT contain any hyphen-prefixed line and must NOT enumerate any obligation.
Do not write a disclaimer; the application appends one.
Reply in ${langName}.`;

    const result = await callWorker({ prompt, maxTokens: 500 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setRecError(busy ? t.aiErrorOverloaded : t.aiError);
      setRecLoading(false);
      return;
    }
    const cleaned = (result.text || '')
      .replace(/```[a-z]*\n?/gi, '')
      .replace(/```/g, '')
      .replace(/\*\*/g, '')
      .replace(/\*/g, '')
      .replace(/`/g, '')
      .replace(/^[\s>]*•\s*/gm, '')
      .split('\n')
      .map(line => line.replace(/\s+$/, ''))
      .join('\n')
      .trim();
    const bulletLines = cleaned
      .split('\n')
      .map(line => line.trim())
      .filter(line => /^-\s/.test(line))
      .map(line => line.replace(/^-\s+/, '').trim())
      .filter(Boolean);
    const coveredTitlesList = (entries || [])
      .filter(e => e.applicable === true)
      .map(e => (e.title || '').trim())
      .filter(Boolean);
    const filteredBullets = bulletLines.filter(b => !coveredTitlesList.some(ct => areEquivalent(b, ct)));
    if (bulletLines.length >= 2) {
      if (filteredBullets.length === 0) {
        setRecItems([]);
        setRecFallback(t.aiAllCoveredFallback);
      } else {
        setRecItems(filteredBullets.slice(0, 10));
        setRecFallback('');
      }
    } else {
      setRecItems([]);
      setRecFallback(cleaned);
    }
    setRecLoading(false);
  };

  // ── §11 rule 42 helpers ─────────────────────────────────────────────────
  const renderBlockContent = (hits) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {t.deleteBlockedReferencedBy} {summariseHits(hits, t)}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {hits.map((h, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { setConfirmState(null); onNavigate?.(HOLDER_NAV[h.holderType], null); }}
            style={{ ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 6, padding: '0.35rem 0.6rem', textAlign: 'left', cursor: 'pointer' }}
          >
            {`${holderLabel(t, h.holderType, 1)} ${h.holderRef}`}
          </button>
        ))}
      </div>
    </div>
  );

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteOne = async (entry) => {
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'legal-register', entry.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      setConfirmState({
        message: t.deleteConfirm.replace('{ref}', entry.ref || entry.id),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `legal-register/${entry.id}.json`);
            setEntries(prev => (prev || []).filter(e => e.id !== entry.id));
          } catch {
            setDeleteError(t.deleteError);
          }
        },
      });
      return;
    }
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
    });
  };

  const handleDeleteFromForm = async () => {
    if (editingId === null) return;
    const entry = (entries || []).find(e => e.id === editingId) || { id: editingId, ref: form?.ref };
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'legal-register', entry.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      setConfirmState({
        message: t.deleteConfirm.replace('{ref}', entry.ref || entry.id),
        confirmLabel: t.deleteFormBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `legal-register/${entry.id}.json`);
            setEntries(prev => (prev || []).filter(e => e.id !== entry.id));
            closeForm();
          } catch {
            setDeleteError(t.deleteError);
          }
        },
      });
      return;
    }
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
    });
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (entries === null) {
    return (
      <div style={{ ...FONT_LABEL, padding: '2rem', color: TEXT_MUTED }}>
        {t.loading}
      </div>
    );
  }

  // ── Computed summary ──────────────────────────────────────────────────────
  const total       = entries.length;
  const applicable  = entries.filter(e => e.applicable !== false).length;
  const underReview = entries.filter(e => e.status === 'Under Review').length;

  // ── Filter options & filtered list ───────────────────────────────────────
  const STATUS_OPTIONS = ['Active', 'Under Review', 'Superseded'];
  const STATUS_LABELS  = {
    'Active':       t.statusActive,
    'Under Review': t.statusUnderReview,
    'Superseded':   t.statusSuperseded,
  };

  const filteredEntries = entries.filter(e => {
    if (filterApplicable === 'yes' && e.applicable === false)         return false;
    if (filterApplicable === 'no'  && e.applicable !== false)         return false;
    if (filterStatus && e.status !== filterStatus)                    return false;
    return true;
  });

  const hasErrors = Object.keys(formErrors).length > 0;

  // ── Form view ─────────────────────────────────────────────────────────────
  if (formOpen && form) {
    const isView  = formMode === 'view';
    const cardSt  = { background: BG_SURFACE, border: `1px solid ${STATUS_NEUTRAL.bg}`, borderRadius: 10, padding: '1.5rem 1.75rem' };

    const statuses = [
      { value: 'Active',       label: t.statusActive },
      { value: 'Under Review', label: t.statusUnderReview },
      { value: 'Superseded',   label: t.statusSuperseded },
    ];

    return (
      <div>
        {/* Form header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '0.75rem', marginBottom: '2rem',
        }}>
          <div>
            <button
              onClick={handleFormCancel}
              style={{
                ...FONT_LABEL,
                background: 'none', border: 'none', cursor: 'pointer',
                color: BRAND_PRIMARY, padding: 0,
                display: 'block', marginBottom: '0.5rem',
              }}
            >{t.backToList}</button>
            <h2 style={{
              ...FONT_PAGE_TITLE,
              color: TEXT_HEADING, margin: 0,
            }}>
              {isView
                ? `${t.formTitleView}: ${form.ref}`
                : (editingId === null ? t.formTitleNew : `${t.formTitleEdit}: ${form.ref}`)}
            </h2>
          </div>
        </div>

        {!isView && (
          <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0 0 2rem', letterSpacing: '0.04em' }}>{t.requiredLegend}</p>
        )}

        {formSaveState === 'error' && (
          <div style={{
            ...FONT_LABEL,
            background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`,
            borderRadius: 6, padding: '0.35rem 0.75rem', marginBottom: '1.25rem',
            color: ERROR_TEXT,
          }}>
            {t.saveError}
          </div>
        )}

        {hasErrors && (
          <div style={{
            ...FONT_LABEL,
            background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`,
            borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1.25rem',
            color: ERROR_TEXT,
          }}>
            {t.validationError}
          </div>
        )}

        <fieldset disabled={isView || formSaveState === 'saving'} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            {/* ── Section A — Identification ── */}
            <div style={cardSt}>
              <SectionTitle>{t.sectionA}</SectionTitle>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={labelSt}>{t.fRef} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                  <input
                    type="text" value={form.ref}
                    onChange={e => { setF('ref', e.target.value); clearErr('ref'); }}
                    style={inputSt(formErrors.ref)}
                  />
                </div>
                <div>
                  <label style={{ ...labelSt, ...(formErrors.title ? { color: ERROR_TEXT } : {}) }}>
                    {t.fTitle} <span style={{ color: ERROR_TEXT }}>{t.required}</span>
                  </label>
                  <input
                    type="text" value={form.title}
                    list="lr-frameworks"
                    onChange={e => { setF('title', e.target.value); clearErr('title'); }}
                    style={inputSt(formErrors.title)}
                  />
                  <datalist id="lr-frameworks">
                    {LEGAL_FRAMEWORKS.map(f => <option key={f} value={f} />)}
                  </datalist>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={labelSt}>{t.fArticle}</label>
                  <input
                    type="text" value={form.article}
                    onChange={e => setF('article', e.target.value)}
                    style={inputSt(false)}
                  />
                </div>
                <div>
                  <label style={labelSt}>{t.fJurisdiction}</label>
                  <input
                    type="text" value={form.jurisdiction}
                    list="lr-jurisdictions"
                    onChange={e => setF('jurisdiction', e.target.value)}
                    style={inputSt(false)}
                  />
                  <datalist id="lr-jurisdictions">
                    {LEGAL_JURISDICTIONS.map(item => <option key={item[lang]} value={item[lang]} />)}
                  </datalist>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelSt}>{t.fDescription}</label>
                <textarea
                  rows={3} value={form.description}
                  onChange={e => setF('description', e.target.value)}
                  placeholder={t.descriptionPlaceholder}
                  style={{ ...inputSt(false), resize: 'vertical' }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={form.applicable}
                    onChange={e => { setF('applicable', e.target.checked); clearErr('applicabilityJustification'); }}
                  />
                  <span style={{ ...FONT_LABEL, color: TEXT_DEFAULT }}>
                    {t.fApplicable}
                  </span>
                </label>
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
                  <label style={{ ...labelSt, marginBottom: 0, ...(formErrors.applicabilityJustification ? { color: ERROR_TEXT } : {}) }}>
                    {t.fApplicabilityJustification}
                    {!form.applicable && <span style={{ color: ERROR_TEXT }}>{t.required}</span>}
                  </label>
                  <AISuggestButton
                    onClick={handleAISuggestApplicability}
                    loading={aiSuggesting}
                    disabled={formSaveState === 'saving' || isView || !form.title.trim() || !org.aiProcessingConsent}
                    lang={lang}
                  />
                  {!org.aiProcessingConsent && <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>{t.aiConsentRequired}</span>}
                </div>
                {aiError && <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '-0.5rem 0 1rem' }}>{aiError}</p>}
                <textarea
                  rows={2} value={form.applicabilityJustification}
                  onChange={e => { setF('applicabilityJustification', e.target.value); clearErr('applicabilityJustification'); }}
                  style={{ ...inputSt(formErrors.applicabilityJustification), resize: 'vertical' }}
                />
              </div>
            </div>

            {/* ── Section B — Relationships ── */}
            <div style={cardSt}>
              <SectionTitle>{t.sectionB}</SectionTitle>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelSt}>
                  {t.fLinkedControls}
                  <InfoBadge text={t.ttLinkedControls} />
                </label>
                <EntitySelector
                  items={controlOptions}
                  selectedIds={form.linkedControlIds}
                  onAdd={(id) => setF('linkedControlIds', [...form.linkedControlIds, id])}
                  onRemove={(id) => setF('linkedControlIds', form.linkedControlIds.filter(x => x !== id))}
                  placeholder={t.controlPlaceholder}
                  disabled={isView || formSaveState === 'saving'}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelSt}>
                  {t.fLinkedDocuments}
                  <InfoBadge text={t.ttLinkedDocuments} />
                </label>
                <EntitySelector
                  items={documentOptions}
                  selectedIds={form.linkedDocumentIds}
                  onAdd={(id) => setF('linkedDocumentIds', [...form.linkedDocumentIds, id])}
                  onRemove={(id) => setF('linkedDocumentIds', form.linkedDocumentIds.filter(x => x !== id))}
                  placeholder={t.documentPlaceholder}
                  disabled={isView || formSaveState === 'saving'}
                  chipBg={STATUS_INFO.bg}
                  chipColor="#1d4ed8"
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelSt}>
                  {t.fLinkedSuppliers}
                  <InfoBadge text={t.ttLinkedSuppliers} />
                </label>
                <EntitySelector
                  items={supplierOptions}
                  selectedIds={form.linkedSupplierIds}
                  onAdd={(id) => setF('linkedSupplierIds', [...form.linkedSupplierIds, id])}
                  onRemove={(id) => setF('linkedSupplierIds', form.linkedSupplierIds.filter(x => x !== id))}
                  placeholder={t.supplierPlaceholder}
                  disabled={isView || formSaveState === 'saving'}
                  chipBg={STATUS_INFO.bg}
                  chipColor="#1d4ed8"
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelSt}>
                  {t.fLinkedAuthorities}
                  <InfoBadge text={t.ttLinkedAuthorities} />
                </label>
                <EntitySelector
                  items={authorityOptions}
                  selectedIds={form.linkedAuthorityIds}
                  onAdd={(id) => setF('linkedAuthorityIds', [...form.linkedAuthorityIds, id])}
                  onRemove={(id) => setF('linkedAuthorityIds', form.linkedAuthorityIds.filter(x => x !== id))}
                  placeholder={t.authorityPlaceholder}
                  disabled={isView || formSaveState === 'saving'}
                  chipBg={STATUS_INFO.bg}
                  chipColor="#1d4ed8"
                />
              </div>

              <div>
                <label style={labelSt}>
                  {t.fTriggeringCustomers}
                  <InfoBadge text={t.ttTriggeringCustomers} />
                </label>
                <EntitySelector
                  items={customerOptions}
                  selectedIds={form.triggeringCustomerIds}
                  onAdd={(id) => setF('triggeringCustomerIds', [...form.triggeringCustomerIds, id])}
                  onRemove={(id) => setF('triggeringCustomerIds', form.triggeringCustomerIds.filter(x => x !== id))}
                  placeholder={t.customerPlaceholder}
                  disabled={isView || formSaveState === 'saving'}
                  chipBg={STATUS_INFO.bg}
                  chipColor="#1d4ed8"
                />
              </div>

            </div>

            {/* ── Section C — Management ── */}
            <div style={cardSt}>
              <SectionTitle>{t.sectionC}</SectionTitle>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={labelSt}>{t.fResponsible}</label>
                  <PeopleSelector
                    orgId={org.id} lang={lang}
                    value={form.responsiblePerson || ''}
                    onChange={val => setF('responsiblePerson', val || null)}
                    disabled={formSaveState === 'saving'}
                  />
                </div>
                <div>
                  <label style={labelSt}>{t.fReviewDate}</label>
                  <input
                    type="date" value={form.reviewDate}
                    onChange={e => setF('reviewDate', e.target.value)}
                    style={inputSt(false)}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelSt}>{t.fStatus}</label>
                <select
                  value={form.status}
                  onChange={e => setF('status', e.target.value)}
                  style={{ ...inputSt(false), maxWidth: 240 }}
                >
                  {statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>

              <div>
                <label style={labelSt}>{t.fNotes}</label>
                <textarea
                  rows={3} value={form.notes}
                  onChange={e => setF('notes', e.target.value)}
                  style={{ ...inputSt(false), resize: 'vertical' }}
                />
              </div>
            </div>

          </div>
        </fieldset>
        <Divider />

        {/* Sticky action bar */}
        {!isView && (
          <div style={{
            position: 'sticky', bottom: 0,
            marginLeft: '-1.5rem', marginRight: '-1.5rem', marginBottom: '-2.5rem',
            marginTop: '1rem',
            padding: '0.85rem 1.5rem', background: BG_SURFACE,
            borderTop: `1px solid ${BORDER_DEFAULT}`, zIndex: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div>
                {editingId !== null && (
                  <button
                    onClick={handleDeleteFromForm}
                    disabled={formSaveState === 'saving'}
                    style={{
                      ...BTN_DESTRUCTIVE_OUTLINE, borderRadius: 8,
                      ...FONT_LABEL,
                      padding: '0.5rem 1rem',
                      cursor: formSaveState === 'saving' ? 'not-allowed' : 'pointer',
                    }}
                  >{t.deleteFormBtn}</button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {dirty && (
                  <span style={{ ...FONT_LABEL, color: UNSAVED_AMBER }}>
                    {t.unsavedLabel}
                  </span>
                )}
                <button
                  onClick={handleFormCancel}
                  disabled={formSaveState === 'saving'}
                  style={{
                    ...BTN_SECONDARY, borderRadius: 8,
                    ...FONT_BUTTON, fontWeight: 400,
                    padding: '0.6rem 1.25rem',
                    cursor: formSaveState === 'saving' ? 'not-allowed' : 'pointer',
                  }}
                >{t.formCancel}</button>
                <button
                  onClick={handleFormSave}
                  disabled={formSaveState === 'saving'}
                  style={{
                    ...BTN_PRIMARY, ...FONT_BUTTON, border: 'none', borderRadius: 8, padding: '0.6rem 1.6rem',
                    cursor: formSaveState === 'saving' ? 'not-allowed' : 'pointer',
                    background: formSaveState === 'saving' ? TEXT_MUTED : formSaveState === 'ok' ? BRAND_PRIMARY_DARK : BTN_PRIMARY.background,
                  }}
                >{formSaveState === 'saving' ? t.saving : formSaveState === 'ok' ? t.saveOk : t.formSave}</button>
              </div>
            </div>
          </div>
        )}
        <ConfirmDialog
          open={confirmState !== null}
          message={confirmState?.message}
          content={confirmState?.content}
          onConfirm={confirmState?.onConfirm}
          onCancel={() => setConfirmState(null)}
          confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
          cancelLabel={t.formCancel || 'Cancel'}
          confirmColor={confirmState?.confirmColor}
          secondaryAction={confirmState?.secondaryAction}
        />
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.75rem',
      }}>
        <div>
          <h2 style={{
            ...FONT_PAGE_TITLE,
            color: TEXT_HEADING, margin: 0,
          }}>{t.title}</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            onClick={openNew}
            style={{
              ...BTN_PRIMARY, ...FONT_BUTTON, border: 'none',
              borderRadius: 8, padding: '0.45rem 1rem', cursor: 'pointer',
            }}
          >{t.newEntry}</button>
        </div>
      </div>

      {/* Delete error */}
      {deleteError && (
        <div style={{
          ...FONT_LABEL,
          background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`,
          borderRadius: 8, padding: '0.65rem 1rem', marginBottom: '1rem',
          color: ERROR_TEXT,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          {deleteError}
          <button
            onClick={() => setDeleteError(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: ERROR_TEXT, fontSize: '1rem', padding: '0 0 0 0.75rem', lineHeight: 1 }}
          >×</button>
        </div>
      )}

      {/* AI recommendations banner (collapsible) */}
      {(() => {
        const generateDisabled = !org.aiProcessingConsent || recLoading;
        const hasContent = recItems.length > 0 || recFallback.length > 0;
        const showBody = recExpanded && (recLoading || hasContent || recError);
        const aiContextEmpty = !((org.aiContext || '').trim());
        const disclaimerText = lang === 'es'
          ? T.es.recommendationsDisclaimer
          : T.en.recommendationsDisclaimer;
        const linkBtnSt = {
          ...FONT_TINY,
          background: 'none', border: 'none', cursor: 'pointer',
          color: TEXT_MUTED,
          padding: 0, textDecoration: 'underline', textDecorationColor: BORDER_DEFAULT,
        };
        return (
          <div style={{
            background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10,
            padding: '1rem 1.25rem', marginBottom: '1.25rem',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <span style={{ ...FONT_SMALL, color: TEXT_DEFAULT, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  {t.recommendationsTitle}
                </span>
                <AISuggestButton
                  onClick={handleGenerateRecommendations}
                  loading={recLoading}
                  disabled={generateDisabled}
                  lang={lang}
                  label={hasContent ? { es: 'Regenerar con IA ✨', en: 'Regenerate with AI ✨' } : undefined}
                />
                {!org.aiProcessingConsent && (
                  <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>{t.aiConsentRequired}</span>
                )}
              </div>
              {(hasContent || recError) && (
                <button
                  onClick={() => setRecExpanded(v => !v)}
                  style={linkBtnSt}
                >
                  {recExpanded ? t.recommendationsHide : t.recommendationsShow}
                </button>
              )}
            </div>
            {aiContextEmpty && (
              <div style={{
                ...FONT_SMALL,
                background: STATUS_WARN.bg, color: STATUS_WARN.text,
                padding: '0.55rem 0.75rem', borderRadius: 8, marginBottom: '0.5rem',
              }}>
                {t.recommendationsContextEmpty}
              </div>
            )}
            <div style={{ ...FONT_SMALL, color: TEXT_MUTED }}>
              {t.recommendationsSummary}
            </div>
            {showBody && (
              <div style={{ marginTop: '0.85rem' }}>
                {recLoading && (
                  <p style={{ ...FONT_BODY, color: TEXT_MUTED, margin: 0 }}>
                    {t.recommendationsLoading}
                  </p>
                )}
                {!recLoading && recError && (
                  <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: 0 }}>
                    {recError}
                  </p>
                )}
                {!recLoading && !recError && hasContent && (
                  <>
                    {recItems.length > 0 ? (
                      <ul style={{ paddingLeft: '1.25rem', margin: 0, listStyleType: 'disc' }}>
                        {recItems.map((item, i) => (
                          <li key={i} style={{ ...FONT_BODY, color: TEXT_DEFAULT, marginBottom: '0.25rem' }}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: 0 }}>
                        {recFallback}
                      </p>
                    )}
                    <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, fontWeight: 700, marginTop: '0.75rem', marginBottom: 0 }}>
                      {disclaimerText}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Summary strip */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.75rem' }}>
        {[
          { label: t.cntTotal,      value: total,       color: TEXT_DEFAULT },
          { label: t.cntApplicable, value: applicable,  color: BRAND_PRIMARY_DARK },
          { label: t.cntUnderReview,value: underReview, color: STATUS_WARN.text },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`,
            borderRadius: 10, padding: '0.9rem 1.25rem',
            flex: 1, minWidth: 88,
          }}>
            <div style={{
              ...FONT_SMALL,
              color: TEXT_DEFAULT, letterSpacing: '0.04em',
              textTransform: 'uppercase', marginBottom: '0.35rem',
            }}>{label}</div>
            <div style={{
              ...FONT_STAT_VALUE, color,
            }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      {entries.length > 0 && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterApplicable}</span>
            <select value={filterApplicable} onChange={e => setFilterApplicable(e.target.value)}
              style={{ ...FONT_LABEL, background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.3rem 0.5rem', color: TEXT_DEFAULT }}>
              <option value="">{t.filterApplicableAll}</option>
              <option value="yes">{t.filterApplicableYes}</option>
              <option value="no">{t.filterApplicableNo}</option>
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterStatus}</span>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{ ...FONT_LABEL, background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.3rem 0.5rem', color: TEXT_DEFAULT }}>
              <option value="">{t.filterAll}</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Empty state */}
      {entries.length === 0 && (
        <div style={{
          background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`,
          borderRadius: 10, padding: '3rem', textAlign: 'center',
        }}>
          <p style={{ ...FONT_BODY, color: TEXT_MUTED, marginBottom: '0.5rem', marginTop: 0 }}>
            {t.emptyTitle}
          </p>
          <p style={{ ...FONT_LABEL, color: TEXT_DISABLED, marginBottom: '1.5rem', marginTop: 0 }}>
            {t.emptyHint}
          </p>
          <button
            onClick={openNew}
            style={{
              ...BTN_PRIMARY, ...FONT_BUTTON, border: 'none',
              borderRadius: 8, padding: '0.65rem 1.5rem', cursor: 'pointer',
            }}
          >{t.newEntry}</button>
        </div>
      )}

      {/* Table */}
      {entries.length > 0 && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${BORDER_DEFAULT}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE }}>
            <thead>
              <tr>
                <th style={{ ...thSt, minWidth: 100 }}>{t.colRef}</th>
                <th style={{ ...thSt, minWidth: 220 }}>{t.colTitle}</th>
                <th style={{ ...thSt, minWidth: 90 }}>{t.colApplicable}</th>
                <th style={{ ...thSt, minWidth: 110 }}>{t.colStatus}</th>
                <th style={{ ...thSt, minWidth: 130 }}>{t.colResponsible}</th>
                <th style={{ ...thSt, minWidth: 110 }}>{t.colReviewDate}</th>
                <th style={{ ...thSt, minWidth: 120 }}>{t.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map(entry => (
                <tr
                  key={entry.id}
                  onMouseEnter={e => { e.currentTarget.style.background = BG_HOVER_LT; }}
                  onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                >
                  <td style={{
                    ...tdSt, ...FONT_LABEL,
                    color: TEXT_MUTED, whiteSpace: 'nowrap',
                  }}>{entry.ref}</td>
                  <td style={{ ...tdSt, fontWeight: 600, color: TEXT_HEADING, maxWidth: 280 }}>
                    {entry.title}
                  </td>
                  <td style={tdSt}>
                    <ApplicableBadge applicable={entry.applicable !== false} t={t} />
                  </td>
                  <td style={tdSt}>
                    <LRStatusBadge status={entry.status} t={t} />
                  </td>
                  <td style={{ ...tdSt, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                    {resolvePerson(entry.responsiblePerson, people) || '—'}
                  </td>
                  <td style={{
                    ...tdSt, ...FONT_LABEL,
                    color: TEXT_DEFAULT,
                  }}>
                    {(() => {
                      if (!entry.reviewDate) return '—';
                      const badge = entry.status === 'Superseded' ? null : dateStatus(entry.reviewDate);
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                          <span style={{ whiteSpace: 'nowrap' }}>{entry.reviewDate}</span>
                          {badge && (
                            <span style={{
                              ...FONT_TINY,
                              fontWeight: 600,
                              background: badge === 'overdue' ? STATUS_BAD.bg : STATUS_WARN.bg,
                              color:      badge === 'overdue' ? STATUS_BAD.text : STATUS_WARN.text,
                              borderRadius: 4,
                              padding: '0.15rem 0.45rem',
                              whiteSpace: 'nowrap',
                            }}>
                              {badge === 'overdue' ? t.badgeOverdue : t.badgeSoon}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={tdSt}>
                    <div style={{ display: 'flex', gap: '0.35rem' }}>
                      <button
                        onClick={() => openView(entry)}
                        style={{ ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 5, padding: '0.2rem 0.6rem', cursor: 'pointer' }}
                      >{t.viewBtn}</button>
                      <button
                        onClick={() => openEdit(entry)}
                        style={{ ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 5, padding: '0.2rem 0.6rem', cursor: 'pointer' }}
                      >{t.editBtn}</button>
                      <button
                        onClick={() => handleDeleteOne(entry)}
                        style={{ ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_LABEL, borderRadius: 5, padding: '0.2rem 0.6rem', cursor: 'pointer' }}
                      >{t.deleteBtn}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Divider />
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        content={confirmState?.content}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
        confirmColor={confirmState?.confirmColor}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
